import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { claimSavedCache, clearSavedCache } from "@/lib/queryCache";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = checking, false = anon, obj = authed
    const [loading, setLoading] = useState(true);

    const check = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            claimSavedCache(data.id);
            setUser(data);
        } catch {
            setUser(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { check(); }, [check]);

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        claimSavedCache(data.user.id);
        setUser(data.user);
        api.post("/dividends/sync").catch(() => {}); // quietly keep dividend income fresh
        return data.user;
    };

    const acceptInvite = async (payload) => {
        const { data } = await api.post("/auth/accept-invite", payload);
        claimSavedCache(data.user.id);
        setUser(data.user);
        return data.user;
    };

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch (e) { console.error("Logout request failed:", e); }
        clearSavedCache(); // never leave portfolio numbers on the device after signing out
        setUser(false);
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, logout, acceptInvite, refresh: check, apiErr }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
